import React, { useMemo, useState } from "react";
import {
  Container,
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  TextField,
  Button,
  Autocomplete,
  Checkbox,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  CircularProgress,
  Alert,
  InputAdornment,
  Stack,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  MessageCircle,
  Phone,
  MessageSquareText,
  Calendar,
  Download,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Search,
  Users,
  Inbox,
  AlertTriangle,
  CheckSquare,
  Square,
  Layers,
  Filter,
  ClipboardCheck,
} from "lucide-react";
import useApiEp from "../hooks/useApiEp";

const PLATFORMS = [
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "#25D366" },
  { id: "voice", label: "Voice", icon: Phone, color: "#2563EB" },
  { id: "rcs", label: "RCS", icon: MessageSquareText, color: "#7C3AED" },
];

const LEAD_QUALIFICATION_OPTIONS = [
  { value: "partial", label: "Partially qualified" },
  { value: "full", label: "Fully qualified" },
];

const STEPS = ["Platform", "Date range", "Campaigns", "Leads"];

const LeadsDownloadsHub = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [platform, setPlatform] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [leadQualification, setLeadQualification] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [leadsSearch, setLeadsSearch] = useState("");

  // ---- Contact groups ----
  const {
    data: groupsData,
    isLoading: groupsLoading,
    isError: groupsIsError,
  } = useApiEp(`/user/group-list`);

  // Optimized: Prevents returning a new array reference on every render
  const groupOptions = useMemo(() => groupsData || [], [groupsData]);

  // ---- Campaigns list ----
  const campaignsEnabled = Boolean(platform && startDate && endDate);

  const {
    data: campaignsData,
    isLoading: campaignsLoading,
    isError: campaignsIsError,
    error: campaignsError,
    refetch: refetchCampaigns,
  } = useApiEp(
    `/api/get-campaigns-list?platform=${
      platform || ""
    }&startDate=${encodeURIComponent(
      startDate || ""
    )}&endDate=${encodeURIComponent(endDate || "")}`,
    campaignsEnabled
  );

  // Optimized: Prevents returning a new array reference on every render
  const campaignOptions = useMemo(() => campaignsData || [], [campaignsData]);

  // ---- Leads ----
  const campaignIdsParam = selectedCampaigns.map((c) => c.campaignId).join(",");

  const leadsUrl = useMemo(() => {
    let url = `/api/get-leads-download?platform=${
      platform || ""
    }&startDate=${encodeURIComponent(
      startDate || ""
    )}&endDate=${encodeURIComponent(
      endDate || ""
    )}&campaignIds=${encodeURIComponent(campaignIdsParam)}`;

    if (selectedGroup) {
      url += `&groupName=${encodeURIComponent(selectedGroup)}`;
    }
    if (platform === "voice" && leadQualification) {
      url += `&leadQualification=${encodeURIComponent(leadQualification)}`;
    }
    return url;
  }, [
    platform,
    startDate,
    endDate,
    campaignIdsParam,
    selectedGroup,
    leadQualification,
  ]);

  const {
    data: leadsData,
    isLoading: leadsLoading,
    isFetching: leadsFetching,
    isError: leadsIsError,
    error: leadsError,
    refetch: fetchLeads,
  } = useApiEp(leadsUrl, false);

  // FIXED: Memoizing the fallback array so the reference remains stable across renders
  const leads = useMemo(() => leadsData || [], [leadsData]);

  const columns = useMemo(() => {
    if (!leads.length) return [];
    const keys = new Set();
    leads.forEach((row) => Object.keys(row).forEach((k) => keys.add(k)));
    return Array.from(keys).sort((a, b) => {
      if (a.toLowerCase() === "phone") return -1;
      if (b.toLowerCase() === "phone") return 1;
      return 0;
    });
  }, [leads]);

  const filteredLeads = useMemo(() => {
    if (!leadsSearch.trim()) return leads;
    const q = leadsSearch.trim().toLowerCase();
    return leads.filter((row) =>
      Object.values(row).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [leads, leadsSearch]);

  const handlePlatformSelect = (id) => {
    setPlatform(id);
    setSelectedCampaigns([]);
    if (id !== "voice") {
      setLeadQualification("");
    }
  };

  const handleNext = () =>
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

  const handleDownloadLeads = async () => {
    setActiveStep(3);
    setPage(0);
    await fetchLeads();
  };

  const handleExportCsv = () => {
    if (!filteredLeads.length) return;
    const header = columns.join(",");
    const rows = filteredLeads.map((row) =>
      columns
        .map((col) => {
          const val = row[col] ?? "";
          const escaped = String(val).replace(/"/g, '""');
          return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
        })
        .join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `leads_${platform}_${startDate}_${endDate}.csv`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const canGoToDateStep = Boolean(platform);
  const dateError = Boolean(startDate && endDate && startDate > endDate);
  const canGoToCampaignStep = Boolean(
    platform && startDate && endDate && !dateError
  );
  const needsQualification = platform === "voice";
  const canDownload = Boolean(
    canGoToCampaignStep &&
      selectedCampaigns.length > 0 &&
      (!needsQualification || Boolean(leadQualification))
  );

  const platformLabel = PLATFORMS.find((p) => p.id === platform)?.label;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Leads Downloads
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Pick a platform, a date range, and the campaigns you need — then pull
          the leads.
        </Typography>
      </Stack>

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 0: Platform */}
        {activeStep === 0 && (
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Select a platform
            </Typography>
            <Grid container spacing={2}>
              {PLATFORMS.map(({ id, label, icon: Icon, color }) => {
                const selected = platform === id;
                return (
                  <Grid item xs={12} sm={4} key={id}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        borderColor: selected ? color : "divider",
                        borderWidth: selected ? 2 : 1,
                        transition: "all 0.15s ease",
                        boxShadow: selected ? `0 0 0 3px ${color}22` : "none",
                      }}
                    >
                      <CardActionArea
                        onClick={() => handlePlatformSelect(id)}
                        sx={{ p: 3 }}
                      >
                        <CardContent
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1.5,
                            p: 0,
                          }}
                        >
                          <Box
                            sx={{
                              width: 56,
                              height: 56,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: `${color}1A`,
                              color,
                            }}
                          >
                            <Icon size={28} />
                          </Box>
                          <Typography fontWeight={600}>{label}</Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
              <Button
                variant="contained"
                disabled={!canGoToDateStep}
                endIcon={<ChevronRight size={18} />}
                onClick={handleNext}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 1: Date range */}
        {activeStep === 1 && (
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Select a date range
            </Typography>
            <Box sx={{ maxWidth: 520 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Start date"
                    type="date"
                    fullWidth
                    size="small"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Calendar size={16} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="End date"
                    type="date"
                    fullWidth
                    size="small"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    error={dateError}
                    helperText={
                      dateError ? "End date must be after start date" : " "
                    }
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Calendar size={16} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}
            >
              <Button
                startIcon={<ChevronLeft size={18} />}
                onClick={handleBack}
              >
                Back
              </Button>
              <Button
                variant="contained"
                disabled={!canGoToCampaignStep}
                endIcon={<ChevronRight size={18} />}
                onClick={handleNext}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 2: Campaigns + contact group + (voice) lead qualification */}
        {activeStep === 2 && (
          <Box>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Select campaigns
            </Typography>

            {campaignsIsError && (
              <Alert
                severity="error"
                icon={<AlertTriangle size={18} />}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<RefreshCw size={14} />}
                    onClick={() => refetchCampaigns()}
                  >
                    Retry
                  </Button>
                }
                sx={{ mb: 2 }}
              >
                {campaignsError?.message ||
                  "Couldn't load campaigns for this selection."}
              </Alert>
            )}

            <Autocomplete
              multiple
              disableCloseOnSelect
              loading={campaignsLoading}
              options={campaignOptions}
              value={selectedCampaigns}
              onChange={(e, value) => setSelectedCampaigns(value)}
              getOptionLabel={(option) => option.campaignName}
              isOptionEqualToValue={(option, value) =>
                option.campaignId === value.campaignId
              }
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={<Square size={18} />}
                    checkedIcon={<CheckSquare size={18} />}
                    checked={selected}
                    sx={{ mr: 1 }}
                  />
                  {option.campaignName}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.campaignName}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    campaignOptions.length
                      ? "Search campaigns..."
                      : "No campaigns found"
                  }
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <Search size={16} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {campaignsLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}
            >
              <Users size={14} />
              <Typography variant="caption" color="text.secondary">
                {selectedCampaigns.length} of {campaignOptions.length} campaigns
                selected
              </Typography>
              {campaignOptions.length > 0 && (
                <Button
                  size="small"
                  onClick={() =>
                    setSelectedCampaigns(
                      selectedCampaigns.length === campaignOptions.length
                        ? []
                        : campaignOptions
                    )
                  }
                >
                  {selectedCampaigns.length === campaignOptions.length
                    ? "Clear all"
                    : "Select all"}
                </Button>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Contact group filter (optional) */}
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              Contact group
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                (optional)
              </Typography>
            </Typography>
            <Autocomplete
              size="small"
              sx={{ maxWidth: 360, width: "100%" }}
              options={groupOptions}
              value={selectedGroup || null}
              onChange={(_, value) => setSelectedGroup(value || "")}
              disabled={groupsLoading}
              clearOnEscape
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={groupsLoading ? "Loading groups..." : "Contact group"}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <Layers size={16} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {groupsIsError && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.5 }}
              >
                Couldn't load contact groups — leads will be pulled without a
                group filter.
              </Typography>
            )}

            {/* Lead qualification (voice only) */}
            {platform === "voice" && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ mb: 1.5 }}
                >
                  Lead qualification
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={leadQualification}
                  onChange={(e, val) => val && setLeadQualification(val)}
                >
                  {LEAD_QUALIFICATION_OPTIONS.map((opt) => (
                    <ToggleButton key={opt.value} value={opt.value}>
                      <Filter size={14} style={{ marginRight: 6 }} />
                      {opt.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                {!leadQualification && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    Required for voice — pick one to continue.
                  </Typography>
                )}
              </Box>
            )}

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}
            >
              <Button
                startIcon={<ChevronLeft size={18} />}
                onClick={handleBack}
              >
                Back
              </Button>
              <Button
                variant="contained"
                disabled={!canDownload}
                endIcon={<Download size={18} />}
                onClick={handleDownloadLeads}
              >
                Download leads
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 3: Review + Leads results */}
        {activeStep === 3 && (
          <Box>
            {/* Review summary */}
            <Paper
              variant="outlined"
              sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: "action.hover" }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1.5 }}
              >
                <ClipboardCheck size={18} />
                <Typography variant="subtitle2" fontWeight={700}>
                  Request summary
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Platform
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {platformLabel}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Date range
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {startDate} → {endDate}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Contact group
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedGroup || "All contacts"}
                  </Typography>
                </Grid>
                {platform === "voice" && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Lead qualification
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {LEAD_QUALIFICATION_OPTIONS.find(
                        (o) => o.value === leadQualification
                      )?.label || "—"}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Campaigns ({selectedCampaigns.length})
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  >
                    {selectedCampaigns.map((c) => (
                      <Chip
                        key={c.campaignId}
                        label={c.campaignName}
                        size="small"
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Leads */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Leads
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  startIcon={<RefreshCw size={14} />}
                  onClick={() => fetchLeads()}
                  disabled={leadsLoading || leadsFetching}
                >
                  Refresh
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Download size={14} />}
                  onClick={handleExportCsv}
                  disabled={!filteredLeads.length}
                >
                  Export CSV
                </Button>
              </Stack>
            </Box>

            {(leadsLoading || leadsFetching) && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            )}

            {!leadsLoading && !leadsFetching && leadsIsError && (
              <Alert
                severity="error"
                icon={<AlertTriangle size={18} />}
                sx={{ mb: 2 }}
              >
                {leadsError?.message ||
                  "Couldn't fetch leads. Please try again."}
              </Alert>
            )}

            {!leadsLoading &&
              !leadsFetching &&
              !leadsIsError &&
              leads.length === 0 && (
                <Box
                  sx={{ textAlign: "center", py: 6, color: "text.secondary" }}
                >
                  <Inbox size={32} style={{ marginBottom: 8 }} />
                  <Typography>No leads found for this selection.</Typography>
                </Box>
              )}

            {!leadsLoading &&
              !leadsFetching &&
              !leadsIsError &&
              leads.length > 0 && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <TextField
                      size="small"
                      placeholder="Search leads..."
                      value={leadsSearch}
                      onChange={(e) => {
                        setLeadsSearch(e.target.value);
                        setPage(0);
                      }}
                      sx={{ maxWidth: 320, flexGrow: 1 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search size={16} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {filteredLeads.length} of {leads.length} leads
                    </Typography>
                  </Box>

                  <TableContainer
                    sx={{
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {columns.map((col) => (
                            <TableCell
                              key={col}
                              sx={{
                                fontWeight: 700,
                                textTransform: "capitalize",
                              }}
                            >
                              {col}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredLeads
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage
                          )
                          .map((row, idx) => (
                            <TableRow key={idx} hover>
                              {columns.map((col) => (
                                <TableCell key={col}>
                                  {row[col] ?? "—"}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    component="div"
                    count={filteredLeads.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                  />
                </>
              )}

            <Box sx={{ mt: 3 }}>
              <Button
                startIcon={<ChevronLeft size={18} />}
                onClick={() => setActiveStep(2)}
              >
                Back to campaigns
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default LeadsDownloadsHub;
